import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import ApiRequester from "../ApiRequester.jsx";
import {handleSubmitActions, formSuccess, formFail} from "../../libs.jsx";
import Button from "./Button.jsx";
import { getSession } from "../../services/session";

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

/*
 * Zone Designer (docs/ZONE_DESIGNER_AUDIT.md, phase 1).
 *
 * ONE source of truth: the Mapbox Draw features. Each zone's name, type, layer and saved
 * zone_id live on its feature's properties, and the save payload is built from
 * draw.getAll() at the moment Save is pressed. `features` is only a mirror of the map for
 * rendering the sidebar, refreshed by syncFeatures() after every change.
 *
 * Why: the editor used to save `backendData`, a snapshot rebuilt only when a Draw event fired
 * or the Data Preview was open. Renames, layer and type changes, rotations, duplicates and
 * sidebar deletes change the map through the Draw API, which fires no event, so with the
 * preview closed they were missing from the save, and a "deleted" zone came back.
 */

/** Where the map opens when the campus has no saved coordinates (PalCare HQ). */
const FALLBACK_CENTER = [-87.9096, 43.0339];

/* The backend still receives names as one comma-joined list and splits it on commas
   (sp_tlGeoZone), so a comma in a name shifts every later zone's type and layer. Refused
   here until the save moves to a JSON payload (audit phase 3). */
const NAME_FORBIDDEN = /,/;

/** The polygon's corners without the closing point (GeoJSON repeats the first at the end). */
function ringCorners(feature) {
    const ring = feature?.geometry?.coordinates?.[0] || [];
    if (ring.length > 1) {
        const [a, b] = [ring[0], ring[ring.length - 1]];
        if (a[0] === b[0] && a[1] === b[1]) return ring.slice(0, -1);
    }
    return ring;
}

/** Do segments p1-p2 and p3-p4 properly cross (not just touch at a shared corner)? */
function segmentsCross(p1, p2, p3, p4) {
    const d = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    const d1 = d(p3, p4, p1), d2 = d(p3, p4, p2), d3 = d(p1, p2, p3), d4 = d(p1, p2, p4);
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** True when any two non-adjacent edges of the polygon cross: a "bow tie" zone. */
export function selfIntersects(corners) {
    const n = corners.length;
    if (n < 4) return false;
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            // Adjacent edges share a corner; the first and last edges are adjacent too.
            if (j === i + 1 || (i === 0 && j === n - 1)) continue;
            if (segmentsCross(corners[i], corners[(i + 1) % n], corners[j], corners[(j + 1) % n])) return true;
        }
    }
    return false;
}

/** "Zone 1", "Zone 2"…: the first not already taken (case-insensitive). */
function nextZoneName(features, base = "Zone") {
    const taken = new Set(features.map((f) => String(f.properties?.zone_name || "").trim().toLowerCase()));
    let n = 1;
    while (taken.has(`${base} ${n}`.toLowerCase())) n++;
    return `${base} ${n}`;
}

/** "Copy of North Wing", then "Copy of North Wing 2"…: unique, so a duplicate never shares a name. */
function copyName(features, name) {
    const taken = new Set(features.map((f) => String(f.properties?.zone_name || "").trim().toLowerCase()));
    const base = `Copy of ${name || "Zone"}`;
    if (!taken.has(base.toLowerCase())) return base;
    let n = 2;
    while (taken.has(`${base} ${n}`.toLowerCase())) n++;
    return `${base} ${n}`;
}

/**
 * The save / export payload, from the given features (draw.getAll().features at save time).
 * Every corner is kept: this used to keep only the first four, so a zone drawn with more
 * corners was saved as a different shape.
 */
export function buildZonePayload(features, defaultZoneTypeId) {
    let pointId = 1;
    return (features || []).map((feature, index) => {
        const p = feature.properties || {};
        return {
            zone_name: String(p.zone_name || `Zone ${index + 1}`).trim(),
            // The saved zone's id, or null for a zone drawn since the last save. The current
            // backend ignores it; the phase 3 save uses it to keep zone ids stable.
            zone_id: p.zone_id ?? null,
            layer: Number.isFinite(Number(p.layer)) ? Number(p.layer) : 0,
            zone_type_id: p.zone_type_id ?? defaultZoneTypeId,
            points: ringCorners(feature).map((coord) => ({
                point_id: pointId++,
                latitude: parseFloat(coord[1].toFixed(8)),
                longitude: parseFloat(coord[0].toFixed(8)),
            })),
        };
    });
}

/** Problems that must be fixed before saving; [] when the layout can be saved. */
export function validateZones(payload) {
    const problems = [];
    const seen = new Map();
    payload.forEach((zone, i) => {
        const label = zone.zone_name || `Zone ${i + 1}`;
        if (!zone.zone_name) problems.push(`Zone ${i + 1} has no name.`);
        if (NAME_FORBIDDEN.test(zone.zone_name)) problems.push(`"${label}": names can't contain a comma.`);
        const key = zone.zone_name.toLowerCase();
        if (key) {
            if (seen.has(key)) problems.push(`"${label}" is used by more than one zone. Zone names must be unique.`);
            seen.set(key, true);
        }
        if (zone.points.length < 3) problems.push(`"${label}" needs at least 3 corners.`);
        else if (selfIntersects(zone.points.map((pt) => [pt.longitude, pt.latitude]))) {
            problems.push(`"${label}" crosses over itself. Move its corners so the edges don't cross.`);
        }
    });
    return problems;
}

const GeofenceTool = (props) => {
    let dData = getSession();

    const mapContainer = useRef(null);
    const map = useRef(null);
    const draw = useRef(null);
    const resizeObserver = useRef(null);
    const [features, setFeatures] = useState([]);
    const [activeFeatureId, setActiveFeatureId] = useState(null);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [editingZoneId, setEditingZoneId] = useState(null); // Which zone name is being edited
    const [editingText, setEditingText] = useState("");
    const [address, setAddress] = useState("");
    const [searchError, setSearchError] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [selectedZoneType, setSelectedZoneType] = useState(0);
    const [saveProblems, setSaveProblems] = useState([]);

    // The default type for new zones: the first type, once the types arrive.
    useEffect(() => {
        if (props.zoneTypes && props.zoneTypes.length > 0) {
            setSelectedZoneType(props.zoneTypes[0].zone_type_id);
        }
    }, [props.zoneTypes]);

    /** Mirror the map into React state, so the sidebar shows exactly what will be saved. */
    const syncFeatures = useCallback(() => {
        if (!draw.current) return;
        const all = draw.current.getAll().features;
        setFeatures(all);
        setActiveFeatureId((id) => (id && all.some((f) => f.id === id) ? id : null));
        setSaveProblems([]);
    }, []);

    /** Set one property on a zone (name, type, layer) and refresh the sidebar. */
    const setZoneProperty = useCallback((featureId, key, value) => {
        if (!draw.current || !draw.current.get(featureId)) return;
        draw.current.setFeatureProperty(featureId, key, value);
        syncFeatures();
    }, [syncFeatures]);

    // Initialise the map once.
    useEffect(() => {
        if (map.current || !mapContainer.current) {
            return;
        }

        const lngNum = Number(props?.longitude);
        const latNum = Number(props?.latitude);
        const hasCenter = props?.longitude != null && props?.latitude != null && props.longitude !== "" &&
            props.latitude !== "" && Number.isFinite(lngNum) && Number.isFinite(latNum);

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/streets-v12",
            center: hasCenter ? [lngNum, latNum] : FALLBACK_CENTER,
            zoom: 17,
            trackResize: true
        });

        const currentMap = map.current;

        resizeObserver.current = new ResizeObserver(() => {
            // requestAnimationFrame debounces rapid resizes.
            window.requestAnimationFrame(() => {
                if (map.current) {
                   map.current.resize();
                }
            });
        });
        resizeObserver.current.observe(mapContainer.current);

        currentMap.on("load", () => {
            currentMap.addControl(new mapboxgl.NavigationControl(), "top-right");

            if (!draw.current) {
                draw.current = new MapboxDraw({
                    displayControlsDefault: true,
                    controls: {
                        trash: true,
                        point: false,
                        line_string: false,
                        polygon: false,
                        combine_features: false,
                        uncombine_features: false,
                    },
                    styles: [
                        {
                            id: "gl-draw-polygon-fill-inactive", type: "fill",
                            filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
                            paint: { "fill-color": "#3bb2d0", "fill-outline-color": "#3bb2d0", "fill-opacity": 0.5 },
                        },
                        {
                            id: "gl-draw-polygon-fill-active", type: "fill",
                            filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
                            paint: { "fill-color": "#fbb03b", "fill-outline-color": "#fbb03b", "fill-opacity": 0.5 },
                        },
                        {
                            id: "gl-draw-polygon-stroke-inactive", type: "line",
                            filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: { "line-color": "#3bb2d0", "line-width": 2 },
                        },
                        {
                            id: "gl-draw-polygon-stroke-active", type: "line",
                            filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: { "line-color": "#fbb03b", "line-dasharray": [0.2, 2], "line-width": 2 },
                        },
                        {
                            id: "gl-draw-vertex-inactive", type: "circle",
                            filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
                            paint: {
                                "circle-radius": 10,
                                "circle-color": "#fff",
                                "circle-stroke-color": "#3bb2d0",
                                "circle-stroke-width": 3,
                                "circle-opacity": 1
                            },
                        },
                        {
                            id: "gl-draw-vertex-active", type: "circle",
                            filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["==", "active", "true"]],
                            paint: {
                                "circle-radius": 12,
                                "circle-color": "#fff",
                                "circle-stroke-color": "#fbb03b",
                                "circle-stroke-width": 3,
                                "circle-opacity": 1
                            },
                        },
                        {
                            // The midpoint handles that add a corner when dragged. Kept visible,
                            // since every corner is now saved.
                            id: "gl-draw-polygon-midpoint", type: "circle",
                            filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
                            paint: { "circle-radius": 5, "circle-color": "#fbb03b" },
                        }
                    ],
                });

                currentMap.addControl(draw.current);
                // Draw's own edits (drag, corner moves, the trash control) fire these; the
                // sidebar's edits call syncFeatures themselves.
                currentMap.on("draw.create", syncFeatures);
                currentMap.on("draw.update", syncFeatures);
                currentMap.on("draw.delete", syncFeatures);
                currentMap.on("draw.selectionchange", (e) => {
                    setActiveFeatureId(e.features.length > 0 ? e.features[0].id : null);
                });
            }
            setMapInitialized(true);
        });

        currentMap.on('error', (e) => {
            console.error("Mapbox Error:", e);
        });

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
                resizeObserver.current = null;
            }
            if (currentMap) {
                currentMap.remove();
            }
            map.current = null;
            draw.current = null;
            setMapInitialized(false);
        };
    // The map is created once; its centre comes from the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Add a square zone at the map's centre, with a unique name.
    const addSquare = () => {
        if (!draw.current) return;

        const center = map.current.getCenter();
        const offset = 0.0005; // half the square's side, in degrees

        const coordinates = [
            [center.lng - offset, center.lat - offset],
            [center.lng + offset, center.lat - offset],
            [center.lng + offset, center.lat + offset],
            [center.lng - offset, center.lat + offset],
            [center.lng - offset, center.lat - offset] // Close the polygon
        ];

        const [id] = draw.current.add({
            type: "Feature",
            properties: {
                shape: "square",
                zone_type_id: selectedZoneType,
                zone_name: nextZoneName(draw.current.getAll().features),
                layer: 0,
                zone_id: null
            },
            geometry: {
                type: "Polygon",
                coordinates: [coordinates]
            }
        });
        syncFeatures();
        if (id) {
            draw.current.changeMode("simple_select", {featureIds: [id]});
            setActiveFeatureId(id);
        }
    };

    // Rotate a point around a center by an angle (in degrees)
    const rotatePoint = (point, center, angleDeg) => {
        const angleRad = (angleDeg * Math.PI) / 180;
        const x = point[0] - center[0];
        const y = point[1] - center[1];
        const xNew = x * Math.cos(angleRad) - y * Math.sin(angleRad);
        const yNew = x * Math.sin(angleRad) + y * Math.cos(angleRad);
        return [xNew + center[0], yNew + center[1]];
    };

    // Rotate the selected zone around its centre.
    const rotateFeature = (angle) => {
        if (!draw.current || !activeFeatureId) return;
        const feature = draw.current.get(activeFeatureId);
        if (!feature) return;

        const corners = ringCorners(feature);
        if (corners.length === 0) return;
        const center = [
            corners.reduce((s, c) => s + c[0], 0) / corners.length,
            corners.reduce((s, c) => s + c[1], 0) / corners.length,
        ];
        const rotated = corners.map((coord) => rotatePoint(coord, center, angle));
        feature.geometry.coordinates[0] = [...rotated, rotated[0]];
        draw.current.add(feature);
        syncFeatures();
    };

    // Duplicate the selected zone, slightly offset, with its own name and no saved id.
    const duplicateFeature = () => {
        if (!draw.current || !activeFeatureId) return;
        const feature = draw.current.get(activeFeatureId);
        if (!feature) return;

        const newFeature = JSON.parse(JSON.stringify(feature));
        delete newFeature.id; // Draw assigns a new one

        // Offset in proportion to the zoom, so the copy is visible but nearby.
        const currentZoom = map.current.getZoom();
        let offset = 0.005;
        if (currentZoom >= 16) offset = 0.0003;
        else if (currentZoom >= 13) offset = 0.001;
        else if (currentZoom >= 10) offset = 0.002;

        newFeature.geometry.coordinates[0] = newFeature.geometry.coordinates[0].map(
            (coord) => [coord[0] + offset, coord[1] + offset]
        );
        newFeature.properties = {
            ...newFeature.properties,
            zone_name: copyName(draw.current.getAll().features, feature.properties?.zone_name),
            // A new zone: it must not claim the original's saved id.
            zone_id: null,
        };

        const [id] = draw.current.add(newFeature);
        syncFeatures();
        if (id) {
            draw.current.changeMode("simple_select", {featureIds: [id]});
            setActiveFeatureId(id);
        }
    };

    // Delete a zone (sidebar × or the Delete button).
    const deleteZone = (featureId) => {
        if (!draw.current || !featureId) return;
        draw.current.delete(featureId);
        if (editingZoneId === featureId) setEditingZoneId(null);
        syncFeatures();
    };

    // Geocode the address and move map to that location
    const geocodeAddress = () => {
        if (!address.trim()) {
            setSearchError("Please enter an address");
            return;
        }

        setSearchError("");

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxgl.accessToken}&limit=1`;

        fetch(url).then((response) => {
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            return response.json();
            }).then((data) => {
                if (data.features && data.features.length > 0) {
                    const [lng, lat] = data.features[0].center;
                    map.current.flyTo({
                        center: [lng, lat],
                        zoom: 15,
                    });
                } else {
                    setSearchError("Address not found");
                }
            }).catch((error) => {
                console.error("Error geocoding address:", error);
                setSearchError("Error finding address. Please try again.");
        });
    };

    /** The payload as it stands on the map right now. */
    const currentPayload = useCallback(
        () => buildZonePayload(draw.current ? draw.current.getAll().features : [], selectedZoneType),
        [selectedZoneType],
    );

    // The preview mirrors the sidebar, so it is always what Save would send.
    const previewData = useMemo(
        () => (showPreview ? buildZonePayload(features, selectedZoneType) : null),
        [showPreview, features, selectedZoneType],
    );

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
    };

    // Export data as JSON
    const exportJson = () => {
        if (!draw.current || features.length === 0) return;
        downloadFile(JSON.stringify(currentPayload(), null, 2), "application/json", "palcare-zones.json");
    };

    // Export data as CSV, one row per corner. Fields are quoted, so a campus name with a
    // comma doesn't shift the columns.
    const exportCsv = () => {
        if (!draw.current || features.length === 0) return;
        const q = (v) => {
            const s = v == null ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = ["Campus Name,Campus ID,Zone ID,Zone Name,Layer,Zone Type ID,Point ID,Lat,Long"];
        currentPayload().forEach((zone) => {
            zone.points.forEach((point) => {
                rows.push([props.campus, props.campusId, zone.zone_id ?? "new", zone.zone_name, zone.layer,
                    zone.zone_type_id, point.point_id, point.latitude, point.longitude].map(q).join(","));
            });
        });
        downloadFile(rows.join("\n") + "\n", "text/csv;charset=utf-8", "palcare-zones.csv");
    };

    // Start editing a zone name
    const startEditing = (featureId) => {
        const feature = draw.current?.get(featureId);
        setEditingZoneId(featureId);
        setEditingText(feature?.properties?.zone_name || "");
    };

    // Save the edited name onto the zone. Blank keeps the current name.
    const handleSaveZoneName = (featureId) => {
        if (editingZoneId !== featureId) return; // Enter then blur: already saved
        const finalName = editingText.trim();
        if (finalName) setZoneProperty(featureId, "zone_name", finalName);
        setEditingZoneId(null);
    };

    // Centre the map on a zone.
    const centerMapOnFeature = (featureId) => {
        if (!map.current || !draw.current) return;
        const feature = draw.current.get(featureId);
        if (!feature) return;
        const corners = ringCorners(feature);
        if (corners.length === 0) return;
        map.current.flyTo({
            center: [
                corners.reduce((s, c) => s + c[0], 0) / corners.length,
                corners.reduce((s, c) => s + c[1], 0) / corners.length,
            ],
            duration: 800,
            essential: true,
            easing: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
        });
    };

    // When a zone item is clicked in the Zone Manager
    const handleZoneItemClick = (featureId) => {
        if (!draw.current || !map.current) return;
        draw.current.changeMode("simple_select", {featureIds: [featureId]});
        setActiveFeatureId(featureId);
        // A moment for the selection to settle before flying.
        setTimeout(() => centerMapOnFeature(featureId), 100);
    };

    // SVG icons for edit and delete buttons
    const EditIcon = () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
        </svg>
    );

    function zoneSubmit(){
        // Built from the map now, not from a snapshot that may predate the last edit.
        const payload = currentPayload();
        const problems = validateZones(payload);
        setSaveProblems(problems);
        if (problems.length) return;

        handleSubmitActions("save-basic-btn", "load-basic-btn");
        ApiRequester({reqEndpoint: "mytransaction", TransType: "zone_edit", Payload: payload, OldPayload: [{}],
                        CampusId: props.campusId, User: dData.user, ClientId: dData.sessionId}).then(data => {
            if (data && data[0]){
                props.setCount(0);
                formSuccess("Zone information saved.", "save-basic-btn", "load-basic-btn");
            }
            else{
                formFail(`Error during save, error code: ${data && data?.length > 1 ? data[1] : ""}`,
                    "save-basic-btn", "load-basic-btn");
            }
        });
    }

    // Load the saved zones once the map and Draw are ready.
    useEffect(() => {
        if (!mapInitialized || !draw.current) return;
        draw.current.deleteAll();

        (props.zoneData || []).forEach((zone, index) => {
            if (!zone || !Array.isArray(zone.points)) {
                console.error(`Invalid zone data at index ${index}:`, zone);
                return;
            }
            const coordinates = zone.points
                .filter((point) => point && typeof point.longitude === 'number' && typeof point.latitude === 'number')
                .map((point) => [point.longitude, point.latitude]);
            if (coordinates.length < 3) {
                console.error(`Zone ${index} has insufficient points:`, coordinates);
                return;
            }
            coordinates.push(coordinates[0]); // Close the polygon

            try {
                draw.current.add({
                    type: "Feature",
                    properties: {
                        shape: "square",
                        zone_type_id: zone.zone_type_id,
                        zone_name: zone.zone_name,
                        layer: zone.layer,
                        zone_id: zone.zone_id
                    },
                    geometry: { type: "Polygon", coordinates: [coordinates] }
                });
            } catch (error) {
                console.error(`Error processing zone ${index}:`, error);
            }
        });
        syncFeatures();
    }, [props.zoneData, mapInitialized, syncFeatures]);

    const zoneTypeOptions = props.zoneTypes || [];

    return (
        <div className="gft-layout">
            <div className="gft-sidebar">
                <h2 className="gft-sidebar__title">PalCare Zone Designer</h2>
                {showHelp ? (
                    <div className="gft-help-panel">
                        <h3 className="gft-section-label">How to Use</h3>
                        <p>
                            <strong>Adding zones:</strong> "Add Square" adds a zone at the
                            centre of the map. Each new zone gets its own name.
                        </p>
                        <p>
                            <strong>Editing:</strong> Select a zone, then drag its corners. Drag
                            the small dot halfway along an edge to add a corner.
                        </p>
                        <p>
                            <strong>Rotation:</strong> Use the rotate buttons when a zone is
                            selected.
                        </p>
                        <p>
                            <strong>Layer:</strong> Where zones overlap, a pendant is placed in the
                            zone with the highest layer.
                        </p>
                        <p>
                            <strong>Saving:</strong> Nothing is stored until you press Save Zones.
                            Zone names must be unique and can't contain a comma.
                        </p>
                        <button className="dp-btn dp-btn--primary" onClick={() => {
                            setShowHelp(false);
                        }}>Close Help</button>
                    </div>
                ) : (
                    <>
                        <div className="gft-group">
                            <label className="gft-section-label">Search Location</label>
                            <div className="gft-search-row">
                                <input type="text" className="dp-drawer__input" placeholder="Enter address" value={address}
                                       onChange={(e) => setAddress(e.target.value)}
                                       onKeyDown={(e) => e.key === "Enter" && geocodeAddress()} />
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={geocodeAddress}>Search</button>
                            </div>
                            {searchError && (
                                <div className="gft-error">{searchError}</div>
                            )}
                        </div>

                        <div className="gft-group">
                            <label className="gft-section-label">Drawing Tools</label>
                            <button className="dp-btn dp-btn--primary" style={{width: '100%'}} onClick={addSquare}
                                    disabled={!mapInitialized}>
                                Add Square
                            </button>
                        </div>

                        <div className="gft-group">
                            <label className="gft-section-label" htmlFor="zoneTypeSelect">Default Zone Type</label>
                            <select
                                id="zoneTypeSelect"
                                className="dp-drawer__select"
                                style={{width: '100%'}}
                                value={selectedZoneType}
                                onChange={(e) => setSelectedZoneType(parseInt(e.target.value))}
                            >
                                {zoneTypeOptions.map((type) => (
                                    <option key={type.zone_type_id} value={type.zone_type_id}>
                                        {type.zone_type_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {features.length > 0 && (
                            <div className="gft-group">
                                <label className="gft-section-label">Edit Selected Zone</label>
                                <div className="gft-btn-grid">
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => rotateFeature(-15)} disabled={!activeFeatureId}>
                                        Rotate ↺ 15°
                                    </button>
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => rotateFeature(15)} disabled={!activeFeatureId}>
                                        Rotate ↻ 15°
                                    </button>
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={duplicateFeature} disabled={!activeFeatureId}>
                                        Duplicate
                                    </button>
                                    <button className="dp-btn dp-btn--danger dp-btn--sm" disabled={!activeFeatureId}
                                            onClick={() => deleteZone(activeFeatureId)}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}

                        {features.length > 0 && (
                            <div className="gft-group">
                                <label className="gft-section-label">Zone Manager</label>
                                <div className="zone-list">
                                    {features.map((feature, index) => {
                                        const p = feature.properties || {};
                                        const displayName = p.zone_name || `Zone ${index + 1}`;
                                        const typeKnown = zoneTypeOptions.some((t) => String(t.zone_type_id) === String(p.zone_type_id));
                                        return (
                                            <div key={feature.id}
                                                 className={`zone-item ${feature.id === activeFeatureId ? "active" : ""}`}
                                                 onClick={() => handleZoneItemClick(feature.id)}>
                                                {editingZoneId === feature.id ? (
                                                    <input type="text"
                                                           className="dp-drawer__input"
                                                           style={{height: '28px', fontSize: 'var(--font-size-xs)'}}
                                                           value={editingText}
                                                           onChange={(e) => setEditingText(e.target.value)}
                                                           onBlur={() => handleSaveZoneName(feature.id)}
                                                           onKeyDown={(e) => {
                                                               if (e.key === "Enter") handleSaveZoneName(feature.id);
                                                               if (e.key === "Escape") setEditingZoneId(null);
                                                           }}
                                                           autoFocus onClick={(e) => e.stopPropagation()}/>
                                                ) : (
                                                    <>
                                                        <div className="zone-info">
                                                            <span className="zone-name">{displayName}</span>
                                                            <div className="zone-controls">
                                                                <div className="zone-layer-input">
                                                                    <label>Layer:</label>
                                                                    <input
                                                                        type="number"
                                                                        className="dp-drawer__input"
                                                                        style={{height: '24px', width: '50px', padding: '0 4px', fontSize: 'var(--font-size-xs)'}}
                                                                        min="0"
                                                                        value={Number(p.layer) || 0}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            setZoneProperty(feature.id, "layer", parseInt(e.target.value) || 0);
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                                <div className="zone-type-input">
                                                                    <label>Type:</label>
                                                                    <select
                                                                        className="dp-drawer__select"
                                                                        style={{height: '24px', padding: '0 4px', fontSize: 'var(--font-size-xs)'}}
                                                                        value={typeKnown ? p.zone_type_id : ""}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            setZoneProperty(feature.id, "zone_type_id", parseInt(e.target.value));
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {/* A saved type this list doesn't know shows as unset
                                                                            rather than silently as the default type. */}
                                                                        {!typeKnown ? <option value="" disabled>Choose a type</option> : null}
                                                                        {zoneTypeOptions.map((type) => (
                                                                            <option key={type.zone_type_id} value={type.zone_type_id}>
                                                                                {type.zone_type_name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="zone-actions">
                                                            <button
                                                                className="edit-button"
                                                                title="Edit zone name"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startEditing(feature.id);
                                                                }}>
                                                                <EditIcon/>
                                                            </button>
                                                            <button className="delete-button" title="Delete zone"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteZone(feature.id);
                                                                    }}>×
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="gft-group">
                            <label className="gft-section-label">Save Options</label>
                            {features.length === 0 && mapInitialized ? (
                                <div className="gft-error">No zones on the map. Saving removes every zone for this campus.</div>
                            ) : null}
                            {saveProblems.length > 0 ? (
                                <div className="gft-error" role="alert">
                                    <strong>Fix before saving:</strong>
                                    <ul style={{margin: '4px 0 0', paddingLeft: '1.1rem'}}>
                                        {saveProblems.map((msg) => <li key={msg}>{msg}</li>)}
                                    </ul>
                                </div>
                            ) : null}
                            <div className="gft-btn-grid">
                                {/* Enabled with no zones too: removing the last zone has to be
                                    saveable, or a campus can never go back to none. */}
                                <Button text={"Save Zones"} class={"dp-btn dp-btn--primary"}
                                                id={"save-basic-btn"}
                                                disabled={!mapInitialized} onClick={() => {
                                                    zoneSubmit()
                                        }}/>
                                <Button type={"button"} class={"dp-btn dp-btn--primary d-none"}
                                                disabled={true}
                                                spinner={true} text={""} id={"load-basic-btn"}/>
                            </div>
                        </div>

                        <div className="gft-group">
                            <label className="gft-section-label">Export Options</label>
                            <div className="gft-btn-grid">
                                <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={exportJson} disabled={!mapInitialized || features.length === 0}>
                                    Download JSON
                                </button>
                                <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={exportCsv} disabled={!mapInitialized || features.length === 0}>
                                    Download CSV
                                </button>
                            </div>
                        </div>

                        {showPreview && (
                            <div className="gft-group">
                                <label className="gft-section-label">Zone Data Preview</label>
                                <pre className="data-display">
                                    {JSON.stringify(previewData, null, 2)}
                                </pre>
                            </div>
                        )}
                        <div className="gft-footer-actions">
                            <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setShowHelp(true)}>Show Help</button>
                            <button className="dp-btn dp-btn--secondary dp-btn--sm"
                                    onClick={() => setShowPreview((v) => !v)}>
                                {showPreview ? "Hide Preview" : "Show Preview"}
                            </button>
                        </div>
                    </>
                )}
            </div>
            <div ref={mapContainer} className="gft-map" />
        </div>
    )
}

export default GeofenceTool;
